import { useMemo } from 'react';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT, OrganizationTypes } from '@/common/constants';
import { useCurrentOrganization } from '@/modules/core/hooks';
import { EmployerStatus } from '@/modules/employer/employer.constants';
import { getIdsFromPath } from '@/utils';

const TIME_SEPARATOR = 'T';

const useEmployerTerminationBanner = () => {
  const {
    observingEmployer,
    observingSubsidiary,
    observingOrCurrentOrganizationData,
  } = useCurrentOrganization({
    isLoadDefaultOrgData: true,
  });
  const currentOrg = useMemo(
    () => {
      if (observingSubsidiary) return observingSubsidiary;
      if (observingEmployer) return observingEmployer;
      if (observingOrCurrentOrganizationData) return {
        name: observingOrCurrentOrganizationData.name,
        id: observingOrCurrentOrganizationData.id,
        type: observingOrCurrentOrganizationData.type,
        status: observingOrCurrentOrganizationData.organization_status_type,
        terminationDate: observingOrCurrentOrganizationData.termination_date,
        path: observingOrCurrentOrganizationData.organization_path,
      };
    },
    [observingEmployer, observingOrCurrentOrganizationData, observingSubsidiary]);

  const isLimitedFeaturesDuringTerminationInProgress = useMemo(() => {
    if (observingOrCurrentOrganizationData) {
      return observingOrCurrentOrganizationData?.is_limited_features_during_termination_in_progress;
    }
    return false;
  }, [observingOrCurrentOrganizationData]);

  const status = useMemo(
    () => currentOrg?.status,
    [currentOrg]);

  const showBanner = useMemo(
    () => (currentOrg?.type === OrganizationTypes.company
    || currentOrg?.type === OrganizationTypes.employer
  || currentOrg?.type === OrganizationTypes.subsidiary)
    && (status === EmployerStatus.PendingTermination
    || status === EmployerStatus.TerminationInProgress
  || status === EmployerStatus.Terminated),
    [currentOrg, status]);

  const terminationDate = useMemo(
    () => currentOrg?.terminationDate
      ? dayjs((currentOrg.terminationDate as string).split(TIME_SEPARATOR)[0]).format(DEFAULT_DATE_FORMAT)
      : undefined,
    [currentOrg]);

  const organizationName = useMemo(
    () => currentOrg?.name,
    [currentOrg]);
  
  const organizationId = useMemo(
    () => {
      if (currentOrg?.type === OrganizationTypes.subsidiary) {
        const { employerId } = getIdsFromPath(currentOrg.path);
        return employerId;
      }
      return currentOrg?.id;
    },
    [currentOrg]);

  const bannerHeight: string | undefined = useMemo(() => showBanner ? '56px' : undefined, [showBanner]);

  const isTerminatedOrInProgress = useMemo(() => {
    return status === EmployerStatus.TerminationInProgress || status === EmployerStatus.Terminated;
  }, [status]);

  const isLimitedUseWhileTerminationInProgress = useMemo(() => {
    return status === EmployerStatus.Terminated
      || (status === EmployerStatus.TerminationInProgress && isLimitedFeaturesDuringTerminationInProgress);
  }, [isLimitedFeaturesDuringTerminationInProgress, status]);
    
  return {
    status,
    showBanner,
    terminationDate,
    bannerHeight,
    organizationName,
    organizationId,
    isTerminatedOrInProgress,
    isLimitedFeaturesDuringTerminationInProgress,
    isLimitedUseWhileTerminationInProgress,
  };
};

export default useEmployerTerminationBanner;
